import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataTable, { TableColumn } from "react-data-table-component";
import AdminDashboardLayout from "../../components/admin/AdminDashboardLayout";
import DashboardCard from "../../components/common/DashboardCard";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { permissionCheckerService } from "../../services/permission.checker.service";
import {
    adminCarrierService,
    CarrierServiceItem,
    GetCarrierServicesParams,
    UpdateCarrierServiceStatusData
} from "../../services/admin.carrier.service";
import { errorService } from "../../services/error.service";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faTruck,
    faSearch,
    faCircleNotch,
    faTriangleExclamation,
    faBoxOpen,
    faEdit
} from "@fortawesome/free-solid-svg-icons";
import EditCarrierServiceModal from "../../components/admin/EditCarrierServiceModal";
import ServiceCountryDataModal from "../../components/admin/ServiceCountryDataModal";
import CarrierStoresModal from "../../components/admin/CarrierStoresModal";
import styles from "../../styles/pages/AdminCarrierServicesPage.module.css";
import btnStyles from "../../styles/components/Button.module.css";
import Swal from "sweetalert2";

const AdminCarrierServicesPage: React.FC = () => {
    const navigate = useNavigate();
    const { adminData } = useAdminAuth();

    useEffect(() => {
        if (!permissionCheckerService.checkPermission(adminData, "CARRIERSERVICES", "READ")) {
            navigate("/admin/dashboard", { replace: true });
        }
    }, [adminData, navigate]);

    const [services, setServices] = useState<CarrierServiceItem[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [totalRows, setTotalRows] = useState(0);
    const [perPage, setPerPage] = useState(10);
    const [currentPage, setCurrentPage] = useState(1);

    // Filter states
    const [searchText, setSearchText] = useState("");
    const [statusFilter, setStatusFilter] = useState<string>("");

    // Modal states
    const [showEditModal, setShowEditModal] = useState(false);
    const [selectedService, setSelectedService] = useState<CarrierServiceItem | null>(null);
    const [showCountryModal, setShowCountryModal] = useState(false);
    const [selectedCountryService, setSelectedCountryService] = useState<CarrierServiceItem | null>(null);
    const [showStoresModal, setShowStoresModal] = useState(false);
    const [selectedStoresService, setSelectedStoresService] = useState<CarrierServiceItem | null>(null);

    const handleShowCountryData = (service: CarrierServiceItem) => {
        setSelectedCountryService(service);
        setShowCountryModal(true);
    };

    const handleShowStores = (service: CarrierServiceItem) => {
        setSelectedStoresService(service);
        setShowStoresModal(true);
    };

    const getStoreCount = (service: CarrierServiceItem) => {
        if (!service.stores) return 0;
        return Object.values(service.stores).reduce((acc, curr) => acc + curr.length, 0);
    };

    const fetchServices = async (page: number, size: number) => {
        try {
            setLoading(true);
            setError(null);

            const params: GetCarrierServicesParams = {
                page: page,
                pageSize: size,
                status: statusFilter !== "" ? parseInt(statusFilter) : undefined,
                SearchString: searchText || undefined,
            };

            const response = await adminCarrierService.getCarrierServices(params);

            if (response.statusCode === 200) {
                setServices(response.data);
                setTotalRows(response.pagination.totalCount);
            } else {
                setError(errorService.getDisplayMessage(response));
            }
        } catch (err: any) {
            console.error("Error fetching admin carrier services:", err);
            setError(errorService.getDisplayMessage(err));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchServices(1, perPage);
    }, []);

    const handlePageChange = (page: number) => {
        setCurrentPage(page);
        fetchServices(page, perPage);
    };

    const handlePerRowsChange = async (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        fetchServices(page, newPerPage);
    };

    const handleSearch = (e: React.FormEvent) => {
        e.preventDefault();
        setCurrentPage(1);
        fetchServices(1, perPage);
    };

    const handleChangeStatus = async (service: CarrierServiceItem) => {
        const newStatus = service.status == 1 ? 0 : 1;
        const actionText = newStatus ? "activar" : "desactivar";

        const result = await Swal.fire({
            title: `¿${newStatus ? "Activar" : "Desactivar"} servicio?`,
            text: `¿Estás seguro que deseas ${actionText} el servicio "${service.name}"?`,
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Sí, cambiar",
            cancelButtonText: "Cancelar",
            confirmButtonColor: "var(--elPaket-primary-color, #e84d30)",
        });

        if (result.isConfirmed) {
            try {
                const response = await adminCarrierService.changeCarrierServiceStatus({
                    IdCarrierService: service.id,
                    Status: newStatus
                });

                if (response.statusCode === 200) {
                    await Swal.fire({
                        icon: "success",
                        title: "¡Actualizado!",
                        text: `El servicio ha sido ${newStatus ? "activado" : "desactivado"} correctamente.`,
                        confirmButtonText: "Aceptar",
                        confirmButtonColor: "var(--elPaket-primary-color, #e84d30)",
                    });
                    fetchServices(currentPage, perPage);
                } else {
                    errorService.showErrorAlert(response, "Error al actualizar estado");
                }
            } catch (err: any) {
                console.error("Error changing carrier service status:", err);
                errorService.showErrorAlert(err, "Error de conexión");
            }
        }
    };

    const handleEdit = (service: CarrierServiceItem) => {
        setSelectedService(service);
        setShowEditModal(true);
    };

    const columns: TableColumn<CarrierServiceItem>[] = [
        {
            name: "ID",
            selector: (row) => row.id,
            sortable: true,
            cell: (row) => (
                <div className={styles.orderNumber}>
                    {row.id}
                </div>
            ),
            width: "100px",
        },
        {
            name: "Courier",
            selector: (row) => row.carrier,
            sortable: true,
            cell: (row) => {
                const storeCount = getStoreCount(row);
                return row.isMain && storeCount > 0 ? (
                    <button
                        onClick={() => handleShowStores(row)}
                        className={styles.serviceBadge}
                    >
                        {row.carrier}
                    </button>
                ) : (
                    <span className={styles.carrierText}>{row.carrier}</span>
                );
            },
            width: "150px",
        },
        {
            name: "Servicio",
            selector: (row) => row.name,
            sortable: true,
            cell: (row) => (
                row.isMain ? (
                    <button
                        onClick={() => handleShowCountryData(row)}
                        className={styles.serviceBadge}
                    >
                        {row.name}
                    </button>
                ) : (
                    <div style={{ fontWeight: "600", color: "#1e293b" }}>
                        {row.name}
                    </div>
                )
            ),
        },
        {
            name: "Contacto",
            selector: (row) => row.contact || "",
            sortable: true,
            cell: (row) => (
                <div style={{ color: "#475569" }}>{row.contact || "-"}</div>
            ),
            width: "150px",
        },
        {
            name: "Cargo",
            selector: (row) => row.charge || "",
            sortable: true,
            cell: (row) => (
                <div style={{ color: "#475569" }}>{row.charge || "-"}</div>
            ),
            width: "120px",
        },
        {
            name: "Email",
            selector: (row) => row.email || "",
            sortable: true,
            cell: (row) => (
                <div style={{ color: "#475569" }}>{row.email || "-"}</div>
            ),
            width: "180px",
        },
        {
            name: "Teléfono",
            selector: (row) => row.phoneNumber || "",
            sortable: true,
            cell: (row) => (
                <div style={{ color: "#475569" }}>{row.phoneNumber || "-"}</div>
            ),
            width: "130px",
        },
        {
            name: "Cargo / Margen",
            cell: (row) => {
                if (!row.isMain) return <div style={{ color: "#94a3b8" }}>-</div>;
                if (row.rate === null || row.rate === undefined) return <div style={{ color: "#94a3b8" }}>-</div>;
                
                if (row.isFix) {
                    return (
                        <div style={{ fontWeight: "600", color: "var(--elPaket-primary-color, #e84d30)" }}>
                            +${Number(row.rate).toFixed(2)}
                        </div>
                    );
                }
                return (
                    <div style={{ fontWeight: "600", color: "var(--elPaket-primary-color, #e84d30)" }}>
                        +{row.rate}%
                    </div>
                );
            },
            width: "130px",
            center: true,
        },
        {
            name: "Estado",
            selector: (row) => row.status,
            sortable: true,
            cell: (row) => (
                <div className={styles.switchContainer}>
                    <label className={styles.switch}>
                        <input
                            type="checkbox"
                            checked={row.status === 1}
                            disabled={!permissionCheckerService.checkPermission(adminData, "CARRIERSERVICES", "UPDATE SERVICE")}
                            onChange={() => handleChangeStatus(row)}
                        />
                        <span className={styles.slider}></span>
                    </label>
                </div>
            ),
            width: "110px",
            center: true,
        },
        {
            name: "Acciones",
            cell: (row) => (
                <div className={styles.actionsContainer}>
                    {permissionCheckerService.checkPermission(adminData, "CARRIERSERVICES", "UPDATE SERVICE") && (
                        <button
                            className={styles.btnEdit}
                            onClick={() => handleEdit(row)}
                            title="Editar Servicio"
                        >
                            <FontAwesomeIcon icon={faEdit} />
                        </button>
                    )}
                </div>
            ),
            width: "100px",
            center: true,
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: "var(--elPaket-primary-color)",
                color: "white",
                minHeight: "48px",
            },
        },
        headCells: {
            style: {
                color: "var(--brand-black)",
                fontWeight: "700",
                fontSize: "12px",
                textTransform: "uppercase" as any,
                letterSpacing: "0.5px",
            },
        },
        rows: {
            style: {
                minHeight: "70px",
                "&:hover": {
                    backgroundColor: "#f8fafc !important",
                },
            },
        },
    };

    return (
        <AdminDashboardLayout>
            <div className={styles.adminCarrierServicesContainer}>
                <DashboardCard
                    icon={faTruck}
                    title="Servicios de Courier"
                    description="Administra los servicios de transporte disponibles y sus tiempos estimados de entrega."
                />

                <div className={styles.tableWrapper}>
                    <form className={styles.tableActions} onSubmit={handleSearch}>
                        <div className={styles.filterSection}>
                            <div className={styles.filterGroup}>
                                <label>Estado</label>
                                <select
                                    className={styles.filterSelect}
                                    value={statusFilter}
                                    onChange={(e) => setStatusFilter(e.target.value)}
                                >
                                    <option value="">Todos</option>
                                    <option value="1">Activos</option>
                                    <option value="0">Inactivos</option>
                                </select>
                            </div>
                        </div>

                        <div className={styles.searchRow}>
                            <div className={styles.searchWrapper}>
                                <FontAwesomeIcon icon={faSearch} className={styles.searchIcon} />
                                <input
                                    type="text"
                                    placeholder="Buscar por nombre de servicio o courier..."
                                    className={styles.searchInput}
                                    value={searchText}
                                    onChange={(e) => setSearchText(e.target.value)}
                                />
                            </div>
                            <button type="submit" className={btnStyles.primary}>
                                <FontAwesomeIcon icon={faSearch} /> Buscar
                            </button>
                        </div>
                    </form>

                    {error ? (
                        <div className={styles.emptyContainer}>
                            <FontAwesomeIcon icon={faTriangleExclamation} size="3x" style={{ color: "#ef4444", marginBottom: "16px" }} />
                            <h3>Error de Carga</h3>
                            <p>{error}</p>
                            <button className={btnStyles.primary} style={{ margin: "20px auto" }} onClick={() => fetchServices(currentPage, perPage)}>
                                Reintentar
                            </button>
                        </div>
                    ) : services.length === 0 && !loading ? (
                        <div className={styles.emptyContainer}>
                            <FontAwesomeIcon icon={faTruck} size="3x" style={{ marginBottom: "16px", opacity: 0.5 }} />
                            <p>No se encontraron servicios de courier con los criterios de búsqueda.</p>
                        </div>
                    ) : (
                        <DataTable
                            columns={columns}
                            data={services}
                            pagination
                            paginationServer
                            paginationTotalRows={totalRows}
                            paginationDefaultPage={currentPage}
                            onChangeRowsPerPage={handlePerRowsChange}
                            onChangePage={handlePageChange}
                            progressPending={loading}
                            progressComponent={
                                <div className={styles.loadingContainer}>
                                    <FontAwesomeIcon icon={faCircleNotch} spin size="3x" className={styles.loadingIcon} />
                                    <p>Cargando lista de servicios...</p>
                                </div>
                            }
                            customStyles={customStyles}
                            responsive
                            highlightOnHover
                            noDataComponent={null}
                        />
                    )}
                </div>
            </div>

            <EditCarrierServiceModal
                show={showEditModal}
                onHide={() => {
                    setShowEditModal(false);
                    setSelectedService(null);
                }}
                onSuccess={() => fetchServices(currentPage, perPage)}
                service={selectedService}
            />

            <ServiceCountryDataModal
                show={showCountryModal}
                onHide={() => {
                    setShowCountryModal(false);
                    setSelectedCountryService(null);
                }}
                service={selectedCountryService}
            />

            <CarrierStoresModal
                show={showStoresModal}
                onHide={() => {
                    setShowStoresModal(false);
                    setSelectedStoresService(null);
                }}
                service={selectedStoresService}
            />
        </AdminDashboardLayout>
    );
};

export default AdminCarrierServicesPage;
